/**
 * Host-side owner of an artifact's review feedback.
 *
 * SitePing runs inside the sandboxed iframe and calls a `SitepingStore`; that
 * store is a thin RPC proxy (see instrument.ts) and *this* is what actually
 * answers it. Keeping the data here rather than in the frame is the whole point
 * of the arrangement: the host holds the Firebase token, so only the host can
 * read and write the workspace, and the frame stays untrusted.
 *
 * Feedback lands in a sibling `<path>.feedback.json` — for `screens/home.html`,
 * `screens/home.feedback.json` — with each comment's crop beside it in
 * `screens/home.feedback/<clientId>.jpg`. Both are ordinary workspace files, so
 * they inherit the file listing and zip export, and the model
 * reads them back with the `fs_read` it already has. No new tool.
 */

import type { SitepingStore } from '@siteping/widget';
import { api } from '@/services/api';
import { STORE_ERROR_CODES, type StoreMethod } from './protocol';

/**
 * The record and input types are not re-exported from the package root, only
 * `SitepingStore` is — so derive them from its signature rather than reaching
 * into `dist/`, which the package's `exports` map does not expose.
 */
export type FeedbackRecord = Awaited<ReturnType<SitepingStore['createFeedback']>> & {
  /**
   * Workspace path of this comment's crop, as `ws://…` — our own field, not
   * SitePing's, and the only form of it that survives to disk.
   *
   * `screenshotUrl` holds a signed URL so the widget can show a thumbnail, but
   * signed URLs expire, so writing one into a durable file would leave the model
   * holding a dead link. The path is stable; the URL is re-signed on demand.
   */
  screenshotPath?: string | null;
  /**
   * Which draft of the artifact this was written against — our own field. See
   * {@link draftIdFor}. Absent on comments predating it, which are therefore
   * treated as belonging to an earlier draft.
   */
  draftId?: string | null;
};
type FeedbackCreateInput = Parameters<SitepingStore['createFeedback']>[0];
type FeedbackQuery = Parameters<SitepingStore['getFeedbacks']>[0];
type FeedbackPage = Awaited<ReturnType<SitepingStore['getFeedbacks']>>;
type FeedbackUpdateInput = Parameters<SitepingStore['updateFeedback']>[1];
type AnnotationRecord = FeedbackRecord['annotations'][number];

/** Shape of the sibling JSON file the model reads. */
export interface ArtifactFeedback {
  version: 1;
  /** Workspace path of the artifact these comments belong to. */
  target: string;
  /**
   * SitePing's own records — message, type, status, author, annotations[] —
   * each with a `screenshotPath` pointing at the crop of the boxed region.
   */
  feedbacks: FeedbackRecord[];
  /** Whatever an opt-in `hostBridge` document chose to store. */
  custom?: unknown;
}

/** Debounce on writes: a comment thread is a burst of small edits. */
const SAVE_DEBOUNCE_MS = 800;

/** Refuse to write a file so large it would wreck the model's context. */
const MAX_FEEDBACK_BYTES = 2 * 1024 * 1024;

/**
 * Strips the `ws://` scheme off a workspace reference.
 *
 * Every workspace API takes a bare path. A `ws://` prefix is not rejected, it
 * just resolves to a different object key, so the read comes back empty and
 * surfaces as the file being unreadable rather than as a malformed path. The
 * model writes tool arguments in scheme form, and those arguments are persisted
 * in the message history, so references keep arriving that way indefinitely —
 * normalizing has to happen wherever one enters the reviewer, not once at the
 * point of writing.
 */
export function toWorkspacePath(ref: string): string {
  return ref.replace(/^ws:\/\//i, '');
}

export function feedbackPathFor(artifactPath: string): string {
  return toWorkspacePath(artifactPath).replace(/\.html?$/i, '') + '.feedback.json';
}

/**
 * Where a comment's crop lives: a folder beside the JSON, one JPEG per comment.
 * For `screens/home.html` → `screens/home.feedback/<clientId>.jpg`.
 *
 * Keyed by `clientId` rather than the record id because `createFeedback` is
 * idempotent on it, so a retried submit overwrites its own crop instead of
 * leaving a stray copy behind.
 */
export function cropPathFor(artifactPath: string, clientId: string): string {
  const safe = clientId.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 64) || 'crop';
  return `${toWorkspacePath(artifactPath).replace(/\.html?$/i, '')}.feedback/${safe}.jpg`;
}

/** Splits a `data:` URL into its MIME type and raw bytes. */
function decodeDataUrl(dataUrl: string): { mime: string; bytes: Uint8Array } | null {
  const match = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(dataUrl);
  if (!match) return null;
  const [, mime, isBase64, payload] = match;
  try {
    if (!isBase64) return { mime, bytes: new TextEncoder().encode(decodeURIComponent(payload)) };
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return { mime, bytes };
  } catch {
    return null;
  }
}

/** Errors carrying the `code` field SitePing's store guards match on. */
function storeError(message: string, code: string): Error & { code: string } {
  const err = new Error(message) as Error & { code: string };
  err.code = code;
  return err;
}

/**
 * Revives the ISO strings written to disk back into the `Date` values
 * `FeedbackRecord` declares. Structured clone preserves them across the
 * postMessage hop, so this is the only place conversion is needed.
 */
function reviveRecord(raw: unknown): FeedbackRecord | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.message !== 'string') return null;
  const toDate = (v: unknown): Date => (typeof v === 'string' ? new Date(v) : new Date(0));
  const annotations = Array.isArray(r.annotations)
    ? (r.annotations as Record<string, unknown>[]).map((a) => ({
        ...a,
        createdAt: toDate(a.createdAt),
      }))
    : [];
  return {
    ...(r as unknown as FeedbackRecord),
    createdAt: toDate(r.createdAt),
    updatedAt: toDate(r.updatedAt),
    resolvedAt: typeof r.resolvedAt === 'string' ? new Date(r.resolvedAt) : null,
    annotations: annotations as AnnotationRecord[],
  };
}

/**
 * Identifies the draft a comment was written against.
 *
 * Comments live in a file named after the artifact, and the model rewrites that
 * same path for every draft — so without this every pin from every past round
 * reappears on each new draft, whether or not it was addressed. Content-derived
 * rather than a counter: there is nothing to increment, and the artifact's bytes
 * are the only honest definition of "which draft is this".
 *
 * FNV-1a, because this distinguishes drafts rather than defending against
 * anything, and a synchronous 32-bit hash over a few hundred KB is instant while
 * SubtleCrypto would make every call site async.
 */
export function draftIdFor(html: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < html.length; i++) {
    hash ^= html.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export interface FeedbackStoreOptions {
  threadId: string;
  artifactPath: string;
  /** Fresh Firebase token for the multipart upload, which bypasses `api`. */
  getToken: () => Promise<string | null>;
  /** Notified after every mutation so the host can render a comment count. */
  onChange?: (records: FeedbackRecord[]) => void;
  onError?: (message: string) => void;
  /** The draft being reviewed; see {@link draftIdFor}. */
  draftId?: string;
  /** Show comments from earlier drafts too, for looking back at a past round. */
  includeAllDrafts?: boolean;
}

export class ArtifactFeedbackStore {
  private records: FeedbackRecord[] = [];
  private custom: unknown = undefined;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saving = false;
  private dirty = false;
  private disposed = false;

  private draftId: string | null;
  private readonly includeAllDrafts: boolean;

  constructor(private readonly opts: FeedbackStoreOptions) {
    this.draftId = opts.draftId ?? null;
    this.includeAllDrafts = opts.includeAllDrafts ?? false;
  }

  /**
   * Names the draft being reviewed, once its bytes are in hand.
   *
   * Separate from construction because the store is created before the artifact
   * is fetched, and the draft is derived from the artifact.
   */
  setDraft(draftId: string): void {
    this.draftId = draftId;
  }

  get feedbackPath(): string {
    return feedbackPathFor(this.opts.artifactPath);
  }

  get all(): FeedbackRecord[] {
    return this.records;
  }

  get customState(): unknown {
    return this.custom;
  }

  /**
   * Loads existing feedback. A missing file is the normal first-visit case, not
   * an error, so any failure here just starts from empty rather than blocking
   * the review.
   */
  async load(): Promise<void> {
    try {
      const data = await api.get<{ type: string; content?: string }>(
        `/chat/threads/${this.opts.threadId}/workspace/preview`,
        { path: this.feedbackPath },
      );
      if (data?.type !== 'text' || !data.content) return;
      const parsed = JSON.parse(data.content) as ArtifactFeedback;
      if (Array.isArray(parsed?.feedbacks)) {
        this.records = parsed.feedbacks
          .map(reviveRecord)
          .filter((r): r is FeedbackRecord => r !== null);
      }
      this.custom = parsed?.custom;
      this.opts.onChange?.(this.records);
    } catch {
      // No feedback file yet, or it is unreadable — start clean.
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      // A pending edit would otherwise be lost on navigate-away.
      void this.flush();
    }
  }

  /** Dispatches an RPC call from the frame. Unknown methods are rejected. */
  async handle(method: StoreMethod, args: unknown[]): Promise<unknown> {
    switch (method) {
      case 'createFeedback':
        return this.createFeedback(args[0] as FeedbackCreateInput);
      case 'getFeedbacks':
        return this.getFeedbacks(args[0] as FeedbackQuery);
      case 'findByClientId':
        return this.findByClientId(args[0] as string);
      case 'updateFeedback':
        return this.updateFeedback(args[0] as string, args[1] as FeedbackUpdateInput);
      case 'deleteFeedback':
        return this.deleteFeedback(args[0] as string);
      case 'deleteAllFeedbacks':
        return this.deleteAllFeedbacks();
      default:
        throw storeError(`Unknown store method: ${String(method)}`, 'STORE_UNKNOWN_METHOD');
    }
  }

  // ── SitepingStore surface ─────────────────────────────────────────────────

  /**
   * Idempotent on `clientId`: SitePing's contract allows either returning the
   * existing record or throwing a duplicate error, and returning it keeps a
   * retried submit from silently failing in the UI.
   */
  private async createFeedback(data: FeedbackCreateInput): Promise<FeedbackRecord> {
    const existing = this.records.find((r) => r.clientId === data.clientId);
    if (existing) return existing;

    // Awaited so the returned record already carries its crop, and so the JSON
    // is never written referencing a file that has not landed yet.
    const screenshotPath = data.screenshotDataUrl
      ? await this.storeCrop(data.clientId, data.screenshotDataUrl)
      : null;

    const now = new Date();
    const id = crypto.randomUUID();
    const annotations: AnnotationRecord[] = (data.annotations ?? []).map((a) => ({
      ...a,
      id: crypto.randomUUID(),
      feedbackId: id,
      elementId: a.elementId ?? null,
      anchorKey: a.anchorKey ?? null,
      createdAt: now,
    }));

    const record: FeedbackRecord = {
      id,
      type: data.type,
      message: data.message,
      status: data.status,
      projectName: data.projectName,
      url: data.url,
      urlPattern: data.urlPattern ?? null,
      authorName: data.authorName,
      authorEmail: data.authorEmail,
      viewport: data.viewport,
      userAgent: data.userAgent,
      clientId: data.clientId,
      resolvedAt: null,
      createdAt: now,
      updatedAt: now,
      annotations,
      // The widget renders this as a thumbnail; it is refreshed on demand and
      // never persisted, because signed URLs expire.
      screenshotUrl: data.screenshotDataUrl ?? null,
      screenshotPath,
      diagnostics: null,
      draftId: this.draftId,
    };

    this.records = [...this.records, record];
    this.commit();
    return record;
  }

  /** Comments left on the draft currently open. */
  get currentDraft(): FeedbackRecord[] {
    if (!this.draftId || this.includeAllDrafts) return this.records;
    return this.records.filter((r) => (r.draftId ?? null) === this.draftId);
  }

  private async getFeedbacks(query: FeedbackQuery): Promise<FeedbackPage> {
    let list = this.currentDraft;
    if (query?.type) list = list.filter((r) => r.type === query.type);
    if (query?.status) list = list.filter((r) => r.status === query.status);
    if (query?.url) list = list.filter((r) => r.url === query.url);
    if (query?.urlPattern) list = list.filter((r) => r.urlPattern === query.urlPattern);
    if (query?.search) {
      const needle = query.search.toLowerCase();
      list = list.filter((r) => r.message.toLowerCase().includes(needle));
    }

    const total = list.length;
    const sorted = [...list].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    const limit = query?.limit ?? total;
    const page = query?.page ?? 1;
    const start = Math.max(0, (page - 1) * limit);
    const feedbacks = sorted.slice(start, start + limit);
    await this.resolveCropUrls(feedbacks);
    return { feedbacks, total };
  }

  private findByClientId(clientId: string): FeedbackRecord | null {
    return this.records.find((r) => r.clientId === clientId) ?? null;
  }

  private updateFeedback(id: string, data: FeedbackUpdateInput): FeedbackRecord {
    const idx = this.records.findIndex((r) => r.id === id);
    if (idx === -1) throw storeError(`Feedback ${id} not found`, STORE_ERROR_CODES.notFound);
    const updated: FeedbackRecord = {
      ...this.records[idx],
      status: data.status,
      resolvedAt: data.resolvedAt ?? null,
      updatedAt: new Date(),
    };
    this.records = [...this.records.slice(0, idx), updated, ...this.records.slice(idx + 1)];
    this.commit();
    return updated;
  }

  private deleteFeedback(id: string): void {
    const doomed = this.records.filter((r) => r.id === id);
    if (doomed.length === 0) {
      throw storeError(`Feedback ${id} not found`, STORE_ERROR_CODES.notFound);
    }
    this.records = this.records.filter((r) => r.id !== id);
    this.deleteCrops(doomed);
    this.commit();
  }

  private deleteAllFeedbacks(): void {
    this.deleteCrops(this.records);
    this.records = [];
    this.commit();
  }

  // ── hostBridge escape hatch ───────────────────────────────────────────────

  setCustom(payload: unknown): void {
    this.custom = payload;
    this.commit();
  }

  // ── Persistence ───────────────────────────────────────────────────────────

  private commit(): void {
    this.opts.onChange?.(this.records);
    this.dirty = true;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.flush();
    }, SAVE_DEBOUNCE_MS);
  }

  /** Writes the current state. Serialized, with a re-run if edits arrived mid-write. */
  async flush(): Promise<void> {
    if (this.saving || !this.dirty) return;
    this.saving = true;
    this.dirty = false;

    const payload: ArtifactFeedback = {
      version: 1,
      target: this.opts.artifactPath,
      // `screenshotUrl` is dropped on the way to disk. In memory it is a data
      // URL or a signed URL — the first would bloat the file the model reads,
      // the second would be expired by the time it read it. `screenshotPath`
      // survives, and that is what the model opens.
      feedbacks: this.records.map(({ screenshotUrl: _dropped, ...rest }) => ({
        ...rest,
        screenshotUrl: null,
      })),
      ...(this.custom === undefined ? {} : { custom: this.custom }),
    };

    try {
      const body = JSON.stringify(payload, null, 2);
      if (body.length > MAX_FEEDBACK_BYTES) {
        throw new Error('Feedback file is too large to save');
      }
      await this.upload(body);
    } catch (err) {
      this.dirty = true;
      const message = err instanceof Error ? err.message : 'Failed to save feedback';
      this.opts.onError?.(message);
      console.warn('[review] Failed to save feedback:', err);
    } finally {
      this.saving = false;
      if (this.dirty && !this.disposed) {
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => {
          this.saveTimer = null;
          void this.flush();
        }, SAVE_DEBOUNCE_MS);
      }
    }
  }

  /**
   * Writes through the existing multipart workspace/upload route. It takes
   * `multipart/form-data`, which `api` cannot send (it forces a JSON content
   * type), so this attaches the token by hand the way WorkspacePanel does.
   */
  private async upload(body: string): Promise<void> {
    const name = this.feedbackPath.split('/').pop() || 'feedback.json';
    await this.uploadToWorkspace(
      this.feedbackPath,
      new File([body], name, { type: 'application/json' }),
    );
  }

  private async uploadToWorkspace(path: string, file: File): Promise<void> {
    const token = await this.opts.getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const form = new FormData();
    form.append('file', file);
    form.append('path', path);

    const resp = await fetch(`/api/chat/threads/${this.opts.threadId}/workspace/upload`, {
      method: 'POST',
      headers,
      body: form,
    });

    if (!resp.ok) {
      const errBody = await resp.json().catch(() => null);
      throw new Error(errBody?.error?.message || `Save failed (${resp.status})`);
    }
  }

  // ── Crops ─────────────────────────────────────────────────────────────────

  /**
   * Stores a comment's crop as its own workspace file and returns its path.
   *
   * The crop is what makes an annotation legible to the model: the coordinates
   * say where the reviewer drew the box, the crop says what was inside it. It
   * goes to a separate file rather than into the JSON because a base64 JPEG
   * inline would dwarf the text the model actually has to read.
   *
   * Returns null on failure, which costs the comment its crop but never the
   * comment itself.
   */
  private async storeCrop(clientId: string, dataUrl: string): Promise<string | null> {
    const decoded = decodeDataUrl(dataUrl);
    if (!decoded || !decoded.mime.startsWith('image/')) return null;

    const path = cropPathFor(this.opts.artifactPath, clientId);
    try {
      const name = path.split('/').pop() || 'crop.jpg';
      const file = new File([decoded.bytes as BlobPart], name, { type: decoded.mime });
      await this.uploadToWorkspace(path, file);
      return `ws://${path}`;
    } catch (err) {
      console.warn('[review] Failed to store annotation crop:', err);
      return null;
    }
  }

  /**
   * Re-signs crop URLs for the records about to be shown. Deliberately not a
   * `commit()`: this only refreshes an expiring URL held in memory, and marking
   * the file dirty over it would rewrite the workspace on every panel open.
   */
  private async resolveCropUrls(records: FeedbackRecord[]): Promise<void> {
    const pending = records.filter((r) => r.screenshotPath && !r.screenshotUrl);
    if (pending.length === 0) return;

    await Promise.all(
      pending.map(async (record) => {
        const path = String(record.screenshotPath).replace(/^ws:\/\//, '');
        try {
          const { url } = await api.get<{ url: string }>(
            `/chat/threads/${this.opts.threadId}/workspace/download`,
            { path },
          );
          if (url) record.screenshotUrl = url;
        } catch {
          // Thumbnail stays blank; the crop file itself is still on disk for
          // the model to read.
        }
      }),
    );
  }

  /** Best-effort cleanup so deleted comments do not leave orphaned crops. */
  private deleteCrops(records: FeedbackRecord[]): void {
    for (const record of records) {
      if (!record.screenshotPath) continue;
      const path = String(record.screenshotPath).replace(/^ws:\/\//, '');
      void api
        .delete(`/chat/threads/${this.opts.threadId}/workspace?path=${encodeURIComponent(path)}`)
        .catch(() => undefined);
    }
  }
}
